import { router, useHttp, usePage } from "@inertiajs/react";
import type { Subscription } from "@rails/actioncable";
import { t } from "@travily/i18n";
import { notificationsSeenMarkerPath } from "@travily/routes";
import { showToast } from "@ui/components/Toast";
import { useEffect, useRef, useState } from "react";
import type { Notification } from "@/components/NotificationBell/NotificationPanel";
import { getConsumer } from "../lib/cable";
import type { SharedProps } from "../types";

export type { NotificationAction } from "@/components/NotificationBell/NotificationItem";
export type { Notification } from "@/components/NotificationBell/NotificationPanel";

// Broadcasts on the per-account NotificationChannel: a persisted Noticed
// notification (bell), or a transient "your unread messages changed" ping that
// carries no payload (the nav re-fetches the authoritative count).
type NewNotificationMessage = { event: "new_notification"; notification: Notification };
type WebSocketMessage = NewNotificationMessage | { event: "messages_unread_changed" };

// Notification kinds that pop an achievement toast (NotificationSerializer.kind).
const ACHIEVEMENT_KINDS = new Set(["badge_earned"]);

// Shared layout props the server recomputes authoritatively on every render
// (InertiaShares); a partial reload of just these resyncs without a full
// navigation.
const NOTIFICATION_PROPS = ["unseen_notifications_count", "notifications"];
const UNREAD_MESSAGES_PROPS = ["unread_messages_count"];

// Module scope survives PageFrame remounts on Inertia navigations (the channel
// is torn down + recreated per page), so a re-delivered broadcast never
// double-toasts within a session. Bounded so a long session can't grow it
// without limit.
const TOASTED_IDS_CAP = 500;
const toastedNotificationIds = new Set<number>();

function maybeShowAchievementToast({ notification }: NewNotificationMessage) {
  if (!ACHIEVEMENT_KINDS.has(notification.kind)) return;
  if (notification.seen) return;
  if (toastedNotificationIds.has(notification.id)) return;

  if (toastedNotificationIds.size >= TOASTED_IDS_CAP) toastedNotificationIds.clear();
  toastedNotificationIds.add(notification.id);
  // notification.message already names the badge(s) ("You earned the France badge!").
  showToast.achievement(t("passport.badges.toast.title"), notification.message);
}

export interface UseNotificationChannelOptions {
  // Invoked when the recipient's unread-message count changes, so messaging
  // surfaces can live-refresh their conversation list (participations).
  onUnreadChanged?: () => void;
}

export interface UseNotificationChannelResult {
  notifications: Notification[];
  unseenCount: number;
  markSeen: () => void;
}

export function useNotificationChannel(options: UseNotificationChannelOptions = {}): UseNotificationChannelResult {
  const {
    unseen_notifications_count,
    notifications: serverNotifications,
    current_account,
  } = usePage<SharedProps>().props;

  const seenHttp = useHttp({});
  const accountId = current_account?.id;

  // Optimistically zero the badge the instant the bell is opened; the real count
  // comes from the server prop and resyncs on the next partial reload, so this
  // never compounds (the previous server-base + local-delta model over-counted
  // after mark-seen because the stale base was never refreshed).
  const [optimisticallySeen, setOptimisticallySeen] = useState(false);

  // Clear the optimistic flag whenever the server resyncs the count (partial
  // reload or navigation). Comparing against the previous value keeps the
  // effect's dependency genuinely used.
  const lastServerCountRef = useRef(unseen_notifications_count);
  useEffect(() => {
    if (unseen_notifications_count !== lastServerCountRef.current) {
      lastServerCountRef.current = unseen_notifications_count;
      setOptimisticallySeen(false);
    }
  }, [unseen_notifications_count]);

  // Keep the latest callback without resubscribing the channel on every render.
  const onUnreadChangedRef = useRef(options.onUnreadChanged);
  useEffect(() => {
    onUnreadChangedRef.current = options.onUnreadChanged;
  }, [options.onUnreadChanged]);

  // Debounced partial reloads (coalesce bursts of broadcasts into one fetch):
  // one for the bell props, one for the nav unread-messages count.
  const reloadTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const refresh = useRef(() => {
    if (reloadTimerRef.current) clearTimeout(reloadTimerRef.current);
    reloadTimerRef.current = setTimeout(() => router.reload({ only: NOTIFICATION_PROPS }), 150);
  }).current;

  const unreadTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const refreshUnreadMessages = useRef(() => {
    if (unreadTimerRef.current) clearTimeout(unreadTimerRef.current);
    unreadTimerRef.current = setTimeout(() => router.reload({ only: UNREAD_MESSAGES_PROPS }), 150);
  }).current;

  // Debounced too, so a burst of inbound messages coalesces into one
  // consumer-owned refresh (the messaging pages reload participations + the
  // count + mark-read here) instead of one router.reload per ping.
  const unreadChangedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const notifyUnreadChanged = useRef(() => {
    if (unreadChangedTimerRef.current) clearTimeout(unreadChangedTimerRef.current);
    unreadChangedTimerRef.current = setTimeout(() => onUnreadChangedRef.current?.(), 150);
  }).current;

  // A surface that owns its unread refresh takes over entirely; otherwise the
  // hook refreshes the nav count generically. Shared by the live ping and the
  // reconnect resync so neither path can race the other to set the count.
  const syncUnread = useRef(() => {
    if (onUnreadChangedRef.current) notifyUnreadChanged();
    else refreshUnreadMessages();
  }).current;

  const subscriptionRef = useRef<Subscription | null>(null);

  useEffect(() => {
    if (!accountId) return;

    const hasConnected = { current: false };
    const subscription = getConsumer().subscriptions.create(
      { channel: "NotificationChannel", account_id: accountId },
      {
        connected() {
          // The first connect is the initial subscribe (props already fresh);
          // a later connect is a reconnect, where solid_cable does not replay
          // broadcasts missed while the socket was down — so resync BOTH the bell
          // props and the unread-messages count (message pings can be missed too).
          if (hasConnected.current) {
            refresh();
            syncUnread();
          }
          hasConnected.current = true;
        },
        received(data: WebSocketMessage) {
          if (data.event === "messages_unread_changed") {
            // Transient ping (no bell entry). A surface that owns its unread
            // refresh (the messaging pages) takes over entirely — it reloads the
            // count itself and may mark the focused conversation read in the same
            // request, so we must NOT also fire the generic count reload or the
            // two would race to set unread_messages_count. Otherwise refresh the nav.
            syncUnread();
            return;
          }
          if (data.event !== "new_notification") return;
          maybeShowAchievementToast(data);
          // Pull the authoritative count + recent list from the server.
          refresh();
        },
      },
    );
    subscriptionRef.current = subscription;

    return () => {
      subscription.unsubscribe();
      subscriptionRef.current = null;
      if (reloadTimerRef.current) clearTimeout(reloadTimerRef.current);
      if (unreadTimerRef.current) clearTimeout(unreadTimerRef.current);
      if (unreadChangedTimerRef.current) clearTimeout(unreadChangedTimerRef.current);
    };
  }, [accountId, refresh, syncUnread]);

  const markSeen = () => {
    setOptimisticallySeen(true);
    seenHttp.post(notificationsSeenMarkerPath(), { onSuccess: () => refresh() });
  };

  const unseenCount = optimisticallySeen ? 0 : (unseen_notifications_count ?? 0);

  return { notifications: serverNotifications ?? [], unseenCount, markSeen };
}
